import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Mail, Lock } from 'lucide-react';

import { AuthShell } from './AuthShell';
import { loginSchema, type LoginInput } from './schemas';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import { cn } from '../../lib/cn';
import styles from './auth.module.css';

const SSO_TITLE = 'זמין ב-Phase 1';

export function LoginScreen() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message.toLowerCase().includes('email not confirmed')) {
        setFormError('החשבון לא אומת — בדוק מייל');
      } else {
        setFormError('אימייל או סיסמה לא נכונים');
      }
      return;
    }
    await logAudit({ action: 'auth.signed_in' });
    navigate('/');
  });

  return (
    <AuthShell
      title="התחברות"
      subtitle="המשך לעבודה שלך"
      brandSide={{
        title: 'כל העסק שלך · מקום אחד',
        body: 'לידים, פרויקטים, כספים, שיווק — הכל מחובר, הכל בעברית.',
      }}
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="email">אימייל</label>
          <input
            id="email"
            type="email"
            dir="ltr"
            autoComplete="email"
            placeholder="you@company.co.il"
            className={cn(styles.input, errors.email && styles.invalid)}
            {...register('email')}
          />
          {errors.email ? <p className={styles.error}>{errors.email.message}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="password">
            סיסמה
            <Link to="/auth/forgot" className={styles.linkMini}>
              שכחתי
            </Link>
          </label>
          <div className={styles.fieldInput}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              dir="ltr"
              autoComplete="current-password"
              placeholder="••••••••"
              className={cn(styles.input, errors.password && styles.invalid)}
              {...register('password')}
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => { setShowPassword((v) => !v); }}
              aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password ? <p className={styles.error}>{errors.password.message}</p> : null}
        </div>

        <label className={styles.check}>
          <input type="checkbox" {...register('remember')} />
          השאר אותי מחובר 30 יום
        </label>

        {formError ? <p className={styles.formError}>{formError}</p> : null}

        <button type="submit" className={styles.primary} disabled={isSubmitting}>
          המשך
          <ArrowLeft size={14} className="flip-rtl" />
        </button>

        <div className={styles.divider}>
          <span>או</span>
        </div>

        <div className={styles.ssoGroup}>
          <button
            type="button"
            className={styles.ssoBtn}
            disabled
            title={SSO_TITLE}
            onClick={() => { console.warn('SSO ב-Phase 1'); }}
          >
            <Mail size={14} /> Google Workspace
          </button>
          <button
            type="button"
            className={styles.ssoBtn}
            disabled
            title={SSO_TITLE}
            onClick={() => { console.warn('SSO ב-Phase 1'); }}
          >
            <Lock size={14} /> Microsoft 365
          </button>
        </div>

        <p className={styles.switch}>
          אין לך חשבון?{' '}
          <Link to="/auth/signup" className={styles.link}>
            צור חשבון חדש
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
